/**
 * Shared tool type map for AI SDK v6 typed parts.
 *
 * Maps each static chat tool to its input/output types so that
 * `ToolUIPart<ChatTools>` produces discriminated unions like
 * `{ type: 'tool-addPrComment'; ... }` instead of untyped `any`.
 *
 * Exa/MCP tools are dynamically registered and always use the
 * `DynamicToolUIPart` type — they are intentionally excluded here.
 */

import type { BashToolResult } from '@/app/(chat)/api/chat/chat-tools/bash-tool';
import type { CustomAgentToolTypes } from '@/app/(chat)/api/chat/chat-tools/custom-agent-tools';
import type {
  FocusCodeArgs,
  FocusCodeResult,
} from '@/app/(chat)/api/chat/chat-tools/focus-code-tool';
import type { PrContextToolTypes } from '@/app/(chat)/api/chat/chat-tools/pr-context-tools';
import type {
  SetPrFileViewedInput,
  SetPrFileViewedResult,
} from '@/app/(chat)/api/chat/chat-tools/set-pr-file-viewed-tool';
import type { AddPrCommentType } from '@/components/inbox/prs/types/pr-comment-input';

// ── Output type for addPrComment (mirrors the tool's execute return) ──
type AddPrCommentOutput = { response: AddPrCommentType };
type BashToolInput = {
  command: string;
};

// ── Static tool type map ──────────────────────────────────────────────
/**
 * Type map for all statically-registered chat tools.
 *
 * Satisfies `UITools` from the AI SDK so it can be passed as a generic
 * parameter to `ToolUIPart<ChatTools>` and `UIMessage<unknown, UIDataTypes, ChatTools>`.
 */
type ThinkToolInput = { thought: string };
type ThinkToolOutput = Record<string, never>;

export type ChatTools = {
  addPrComment: { input: AddPrCommentType; output: AddPrCommentOutput };
  focusCode: { input: FocusCodeArgs; output: FocusCodeResult };
  setPrFileViewed: { input: SetPrFileViewedInput; output: SetPrFileViewedResult };
  bash: { input: BashToolInput; output: BashToolResult };
  think: { input: ThinkToolInput; output: ThinkToolOutput };
} & PrContextToolTypes &
  CustomAgentToolTypes;

// ── Derived helpers ───────────────────────────────────────────────────

/** Union of all static tool names. */
export type StaticToolName = keyof ChatTools;

/** Type guard that narrows an arbitrary string to a `StaticToolName`. */
export function isStaticTool(name: string): name is StaticToolName {
  return (
    name === 'addPrComment' ||
    name === 'focusCode' ||
    name === 'setPrFileViewed' ||
    name === 'bash' ||
    name === 'think' ||
    name === 'readPrSummary' ||
    name === 'listChangedFiles' ||
    name === 'readChangedFile' ||
    name === 'searchPrArtifacts' ||
    name === 'readRepoMemory' ||
    name === 'listThreads' ||
    name === 'readThread' ||
    name === 'readChecksSummary' ||
    name === 'listCustomAgents' ||
    name === 'proposeCustomAgentChange'
  );
}
